import { useQuery } from '@tanstack/react-query';
import { peticion } from '../api/cliente.js';
import type { CitaCreada, RespuestaDeCitas } from '../api/tipos.js';
import { Aviso } from '../componentes/comunes.js';
import { instanteLargo, nombreDeZona } from '../fechas.js';
import { nombreDeProducto } from '../nombres.js';
import { textos } from '../textos.js';

/** Resumen de la cita recién creada, con la descarga del `.ics` (§9). */
export function PantallaReservada({
  cita,
  zona,
  contactoNombre,
  contactoTelefono,
  alVerMisCitas,
  alReservarOtra,
}: {
  cita: CitaCreada;
  zona: string;
  contactoNombre: string;
  contactoTelefono: string;
  alVerMisCitas: () => void;
  alReservarOtra: () => void;
}) {
  const mesDeLaCita = cita.inicio.slice(0, 7);
  const nombreDelMes = new Intl.DateTimeFormat('es-ES', {
    month: 'long',
    year: 'numeric',
    timeZone: zona,
  }).format(new Date(cita.inicio));
  const fechaDestacada = new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: zona,
  }).format(new Date(cita.inicio));
  const horaDestacada = new Intl.DateTimeFormat('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: zona,
  }).format(new Date(cita.inicio));
  const consulta = useQuery({
    queryKey: ['citas', mesDeLaCita],
    queryFn: () => peticion<RespuestaDeCitas>(`/citas?mes=${mesDeLaCita}`),
  });
  const cupo = consulta.data?.cupoMensual ?? null;

  return (
    <section className="pantalla-reservada" aria-labelledby="titulo-reservada">
      <div className="cabecera-exito">
        <span className="icono-exito" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="m7 12 3.2 3.2L17.5 8" />
          </svg>
        </span>
        <div>
          <p>{textos.reserva.completada}</p>
          <h2 id="titulo-reservada">{textos.reserva.titulo}</h2>
        </div>
      </div>

      <div className="reserva-destacada">
        <div className="reserva-fecha-principal">
          <span>{fechaDestacada}</span>
          <strong>{horaDestacada}</strong>
          <small>{nombreDeZona(zona)}</small>
        </div>
        <div className="reserva-servicio-principal">
          <span>{textos.resumenReserva.servicio}</span>
          <strong>{nombreDeProducto(cita.servicio.nombre)}</strong>
          <small>{cita.duracionMin} minutos con un especialista</small>
        </div>
      </div>

      <dl className="resumen">
        <dt>Servicio</dt>
        <dd>{nombreDeProducto(cita.servicio.nombre)}</dd>
        <dt>Cuándo</dt>
        <dd>
          {instanteLargo(cita.inicio, zona)} · {nombreDeZona(zona)}
        </dd>
        <dt>Duración</dt>
        <dd>{cita.duracionMin} minutos</dd>
        <dt>Llamamos a</dt>
        <dd>
          {contactoNombre} · {contactoTelefono}
        </dd>
      </dl>

      <div className="botonera">
        <a
          className="principal"
          href={`/api/v1/citas/${cita.id}/calendario.ics`}
          download={`cita-${cita.id}.ics`}
        >
          {textos.reserva.descargarIcs}
        </a>
        <button type="button" onClick={alVerMisCitas}>
          {textos.reserva.verMisCitas}
        </button>
        <button type="button" className="enlace" onClick={alReservarOtra}>
          {textos.reserva.otraCita}
        </button>
      </div>

      {consulta.isPending && (
        <p className="ayuda cupo-tras-reserva" role="status">
          {textos.reserva.consultandoCupo}
        </p>
      )}
      {consulta.isError && (
        <div className="cupo-tras-reserva">
          <Aviso tipo="error">
            {textos.reserva.errorCupo}{' '}
            <button type="button" className="enlace" onClick={() => void consulta.refetch()}>
              {textos.aplicacion.reintentar}
            </button>
          </Aviso>
        </div>
      )}
      {cupo !== null && (
        <div className="cupo-tras-reserva">
          <Aviso tipo={cupo.restantes === 0 ? 'atencion' : 'informacion'}>
            {cupo.limite === null
              ? textos.reserva.cupoSinLimite(nombreDelMes)
              : textos.reserva.cupoRestante(cupo.restantes ?? 0, nombreDelMes)}
          </Aviso>
        </div>
      )}
    </section>
  );
}
